<template>
  <div
    id="lc-player"
    class="lc-player"
    :class="{ '-with-countdown': shouldDisplayCountdown }"
    ref="player"
    :style="{ 'background-image': `url(${imageUrl})` }"
  >
    <Countdown v-if="shouldDisplayCountdown" :date="stream.startsAt" />
  </div>
</template>

<script>
import Vue from 'vue';
import Countdown from './Countdown';
import imgixURL from '../helpers/imgix-url';

class Player {
  state = Vue.observable({
    currentTime: 0
  });

  element = 'lc-player';

  get hasPlayableSource() {
    return hasPlayableSource(this.source);
  }

  get sourceStatus() {
    return this.normalizeSourceStatus(this.source);
  }

  constructor(source) {
    this.loadSource(source);
  }

  destroy() {
    this.player && this.player.remove();
    this.player = null;
  }

  seekTo(seconds) {
    this.player.seek(seconds);
  }

  getCurrentTime() {
    return this.player.getPosition();
  }

  update(source) {
    if (
      source.playbackId !== (this.source && this.source.playbackId) ||
      this.normalizeSourceStatus(source) !== this.sourceStatus
    ) {
      this.loadSource(source);
    }
  }

  loadSource(source) {
    this.source = source;

    if (!this.hasPlayableSource) return this.destroy();
    if (!this.player) return this.setupPlayer();

    let cachedCurrentTime = this.state.currentTime;

    this.player.setConfig({ autostart: true });
    this.player.once('play', () => this.seekTo(cachedCurrentTime));
    this.player.load(this.sourceToPlaylistItem(this.source));
  }

  setupPlayer() {
    if (!this.hasPlayableSource) return;

    this.player = jwplayer(this.element).setup({
      aboutlink: 'https://www.livecasts.eu',
      abouttext: 'Livecasts',
      autostart: true,
      cast: {},
      floating: { dismissible: true },
      horizontalVolumeSlider: true,
      mute: false,
      playlist: this.sourceToPlaylistItem(this.source)
    });

    this.player.on('time', ({ currentTime }) => {
      this.state.currentTime = currentTime;
    });
  }

  sourceToPlaylistItem({ playbackId, playbackToken, storyboardToken, image }) {
    return {
      file: `https://stream.mux.com/${playbackId}.m3u8?token=${playbackToken}`,
      image,
      tracks: [
        {
          kind: 'thumbnails',
          file: `https://image.mux.com/${playbackId}/storyboard.vtt?token=${storyboardToken}`
        }
      ]
    };
  }

  normalizeSourceStatus(source) {
    return source.assetStatus === 'ready' ||
      source.assetStatus === 'live_stream_completed'
      ? 'playable'
      : source.assetStatus;
  }
}

export default {
  components: { Countdown },

  props: {
    stream: Object,
    source: Object
  },

  mounted() {
    this.player = new Player({ ...this.source, image: this.imageUrl });
    this.$emit('register', this.player);
  },

  beforeDestroy() {
    this.player.destroy();
  },

  watch: {
    source(source) {
      this.player.update(source);
    }
  },

  computed: {
    imageUrl() {
      return imgixURL(this.stream.cover, { w: 2560, h: 1440, fit: 'crop' });
    },

    shouldDisplayCountdown() {
      return !this.hasPlayableSource && this.stream.startsAt;
    },

    hasPlayableSource() {
      return hasPlayableSource(this.source);
    }
  }
};

function hasPlayableSource(source) {
  return (
    Boolean(source.playbackId) &&
    (source.assetStatus === 'ready' ||
      source.assetStatus === 'live_stream_completed')
  );
}
</script>

<style lang="scss">
@import '../styles/variables';

.lc-player,
.jwplayer {
  background-color: $white-15;
  border-radius: 0.125rem;
}

.lc-player {
  width: 100%;
  padding: 56.25% 0 0 0;
  background-color: $white-15;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;

  &.-with-countdown {
    position: relative;
  }
}

#lc-player {
  video,
  .jw-wrapper,
  .jw-preview,
  .jw-controls,
  .jw-controls-backdrop {
    border-radius: 0.125rem;
  }

  &.jw-flag-floating .jw-wrapper {
    @apply shadow-lg;
  }

  .jw-preview {
    background-color: $white-15;
  }

  .jw-reset-text,
  .jw-reset {
    @apply font-primary;

    font-weight: 500;
  }

  .jw-settings-menu {
    border-radius: 0.125rem;
    max-width: 10rem;
    background-color: rgba($black, 0.8);
  }

  .jw-settings-topbar {
    border-radius: 0.25rem 0.25rem 0 0;
  }

  .jw-slider-time {
    align-items: flex-end;

    .jw-rail,
    .jw-buffer,
    .jw-progress {
      transform: translate(0, -50%) scale(1, 1);
      border-radius: 0.5rem;
    }

    .jw-progress {
      @apply bg-primary;
    }

    .jw-cue {
      @apply bg-primary;

      border: 0.125rem solid rgba($white, 0.8);
      width: 0.5rem;
      height: 0.5rem;
      transform: translate(0, -50%) scale(1);
      border-radius: 50%;

      &:hover {
        transform: translate(0, -50%) scale(1.5);
        border-color: $white;
      }
    }
  }

  .jw-slider-horizontal .jw-slider-container {
    height: 0.25rem;
  }

  .jw-horizontal-volume-container .jw-rail {
    transform: translate(0, -50%) scale(1, 1);
    border-radius: 0.25rem;
  }

  &:not(.jw-flag-small-player)
    .jw-flag-horizontal-slider
    ~ .jw-horizontal-volume-container.jw-open {
    width: 6rem;

    .jw-slider-volume {
      padding-right: 1rem;
    }
  }

  .jw-rail,
  .jw-buffer,
  .jw-progress {
    border-radius: 0.5rem;
  }

  .jw-time-tip,
  .jw-controlbar .jw-tooltip,
  .jw-settings-menu .jw-tooltip {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    margin-bottom: 0.25rem;
    border-radius: 0.25rem;
    box-shadow: none;

    &:after {
      display: none;
    }

    .jw-time-thumb {
      @apply shadow-md;

      transform: scale(0.7);
      transform-origin: center bottom;
      border-radius: 0.25rem;
      border: 2px solid $white;
    }

    .jw-text {
      @apply shadow-md;

      min-width: 0;
      min-height: 0;
      padding: 0.375rem 0.75rem;
      border-radius: 0.25rem;
      background: rgba($black, 0.6);
      color: $white;
      font-size: 0.75rem;
    }
  }

  .jw-rightclick {
    display: none !important;
    pointer-events: none !important;
  }
}
</style>
